import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Plus, Trash2 } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';

interface VariableManagerProps {
  onInsertVariable: (variable: string) => void;
}

// Standard variables that are always available
const standardVariables = [
  { name: 'firstName', description: 'Recipient\'s first name', example: 'John' },
  { name: 'lastName', description: 'Recipient\'s last name', example: 'Doe' },
  { name: 'emailAddress', description: 'Recipient\'s email address', example: 'john.doe@example.com' },
  { name: 'companyName', description: 'Recipient\'s company name', example: 'Acme Corp' },
  { name: 'businessName', description: 'Your business name', example: 'Moyo Tools' },
  { name: 'year', description: 'Current year', example: new Date().getFullYear().toString() },
];

export const VariableManager: React.FC<VariableManagerProps> = ({ onInsertVariable }) => {
  const [customVariables, setCustomVariables] = useState<Array<{ name: string; value: string }>>([]);
  const [newVarName, setNewVarName] = useState('');
  const [newVarValue, setNewVarValue] = useState('');
  
  const addCustomVariable = () => {
    if (newVarName.trim() === '') return;
    
    // Check if variable already exists
    const exists = customVariables.some(v => v.name === newVarName);
    if (exists) {
      // Update existing variable
      setCustomVariables(customVariables.map(v => 
        v.name === newVarName ? { name: newVarName, value: newVarValue } : v
      ));
    } else {
      // Add new variable
      setCustomVariables([...customVariables, { name: newVarName, value: newVarValue }]);
    }
    
    // Reset form
    setNewVarName('');
    setNewVarValue('');
  };
  
  const removeCustomVariable = (name: string) => {
    setCustomVariables(customVariables.filter(v => v.name !== name));
  };
  
  const handleInsertStandardVariable = (variable: string) => {
    onInsertVariable(`{{${variable}}}`);
  };
  
  const handleInsertCustomVariable = (variable: string) => {
    onInsertVariable(`[[${variable}]]`);
  };
  
  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-medium mb-2">Standard Variables</h3>
        <p className="text-sm text-muted-foreground mb-4">
          These variables will be automatically replaced with recipient data when the email is sent.
        </p>
        
        <ScrollArea className="h-[180px] border rounded-md p-2">
          <div className="space-y-3">
            {standardVariables.map((variable) => (
              <div key={variable.name} className="flex justify-between items-center">
                <div>
                  <div className="font-medium text-sm">{`{{${variable.name}}}`}</div>
                  <div className="text-xs text-muted-foreground">{variable.description}</div>
                  <div className="text-xs italic mt-1">Example: {variable.example}</div>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => handleInsertStandardVariable(variable.name)}
                >
                  Insert
                </Button>
              </div>
            ))}
          </div>
        </ScrollArea>
      </div>
      
      <Separator />
      
      <div>
        <h3 className="font-medium mb-2">Custom Variables</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Create your own variables that will be replaced with the specified values.
        </p>
        
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="col-span-1">
            <Label htmlFor="var-name" className="text-xs">Variable Name</Label>
            <Input
              id="var-name"
              placeholder="discount"
              value={newVarName}
              onChange={(e) => setNewVarName(e.target.value)}
              className="mt-1"
            />
          </div>
          <div className="col-span-1">
            <Label htmlFor="var-value" className="text-xs">Value</Label>
            <Input
              id="var-value"
              placeholder="20% OFF"
              value={newVarValue}
              onChange={(e) => setNewVarValue(e.target.value)}
              className="mt-1"
            />
          </div>
          <div className="col-span-1 flex items-end pb-1">
            <Button 
              onClick={addCustomVariable} 
              size="sm" 
              className="w-full flex items-center gap-1"
            >
              <Plus className="h-3 w-3" />
              Add Variable
            </Button>
          </div>
        </div>
        
        {customVariables.length > 0 ? (
          <ScrollArea className="h-[180px] border rounded-md p-2">
            <div className="space-y-3">
              {customVariables.map((variable) => (
                <div key={variable.name} className="flex justify-between items-center">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-sm">[[{variable.name}]]</div>
                      <Badge variant="outline" className="text-xs">
                        {variable.value}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleInsertCustomVariable(variable.name)}
                    >
                      Insert
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => removeCustomVariable(variable.name)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        ) : (
          <div className="border rounded-md py-8 text-center text-muted-foreground">
            No custom variables added yet
          </div>
        )}
      </div>
    </div>
  );
}; 